import React, { useState } from 'react';
import { motion } from 'framer-motion';

/* ---------------- isometric helpers ---------------- */
const TX = 26;
const TY = 13;
const HZ = 22;
const OX = 460;
const OY = 70;

const iso = (x: number, y: number, z = 0): [number, number] => [
  (x - y) * TX + OX,
  (x + y) * TY + OY - z * HZ,
];
const pt = (x: number, y: number, z = 0) => iso(x, y, z).join(',');

/* ---------------- types ---------------- */
interface Building {
  id: string;
  x: number;
  y: number;
  w: number;
  d: number;
  h: number;
  top: string;
  left: string;
  right: string;
  name: string;
  awning?: string;
  roof?: 'flat' | 'peaked' | 'striped';
  stripeA?: string;
  stripeB?: string;
  windows?: number;
  hasAI?: boolean;
}

/* ---------------- buildings registry ---------------- */
const BUILDINGS: Building[] = [
  /* NW quadrant */
  {
    id: 'house-a', name: 'Cottage',
    x: 0.5, y: 0.5, w: 2.2, d: 2.2, h: 1.7,
    top: '#F7E9D5', left: '#E0CDAE', right: '#C8B391',
    roof: 'peaked', awning: '#C97B63', windows: 2,
  },
  {
    id: 'shop-stripe', name: 'Boulangerie',
    x: 3.2, y: 0.6, w: 2.8, d: 1.8, h: 1.7,
    top: '#FFFFFF', left: '#E8E8E8', right: '#D2D2D2',
    roof: 'striped', stripeA: '#D03B3B', stripeB: '#FFFFFF',
    awning: '#D03B3B', windows: 2,
  },
  {
    id: 'house-b', name: 'Studio',
    x: 0.5, y: 3.3, w: 1.8, d: 2.0, h: 1.5,
    top: '#EEF2F6', left: '#D2D8DF', right: '#B9C1CA',
    roof: 'peaked', windows: 2,
  },
  {
    id: 'house-c', name: 'Cafe',
    x: 2.8, y: 3.3, w: 2.0, d: 2.0, h: 1.6,
    top: '#FBE7C6', left: '#E3CFA7', right: '#CCB58A',
    roof: 'peaked', awning: '#D9A05B', windows: 2,
  },

  /* NE quadrant */
  {
    id: 'office-a', name: 'BlueTower',
    x: 9.5, y: 0.5, w: 2.5, d: 2.3, h: 3.2,
    top: '#EAF2FB', left: '#C9DAE9', right: '#A9BFD3',
    roof: 'flat', windows: 4,
  },
  {
    id: 'office-b', name: 'Garage',
    x: 12.3, y: 0.5, w: 2.3, d: 2.0, h: 1.6,
    top: '#F2F3F5', left: '#D5D7DA', right: '#BCBEC2',
    roof: 'flat', windows: 2,
  },
  {
    id: 'warehouse', name: 'Depot',
    x: 11.0, y: 3.0, w: 3.6, d: 2.0, h: 1.4,
    top: '#E6E6E8', left: '#CDCED0', right: '#B6B7B9',
    roof: 'flat', windows: 0,
  },

  /* SW quadrant - AI BUILDING */
  {
    id: 'ai-shop', name: 'NovaMart AI',
    x: 0.5, y: 9.0, w: 4.7, d: 6.0, h: 4.8,
    top: '#FFFFFF', left: '#F1B9CB', right: '#D89AAD',
    roof: 'flat', awning: '#5B8DEF', windows: 5,
    hasAI: true,
  },

  /* SE quadrant */
  {
    id: 'store-red', name: 'Diner',
    x: 9.0, y: 9.5, w: 2.4, d: 2.2, h: 1.8,
    top: '#FFFFFF', left: '#E5E5E5', right: '#CCCCCC',
    awning: '#C24A3E', roof: 'flat', windows: 2,
  },
  {
    id: 'store-green', name: 'Grocer',
    x: 11.7, y: 9.5, w: 1.9, d: 2.0, h: 1.7,
    top: '#FFFFFF', left: '#E2E2E2', right: '#CCCCCC',
    awning: '#5F9E7A', roof: 'flat', windows: 2,
  },
  {
    id: 'store-yellow', name: 'Florist',
    x: 13.9, y: 9.5, w: 1.6, d: 2.0, h: 1.6,
    top: '#FFFFFF', left: '#E2E2E2', right: '#CCCCCC',
    awning: '#E6B84A', roof: 'flat', windows: 1,
  },
];

/* ---------------- primitives ---------------- */

const IsoBox: React.FC<{
  x: number; y: number; w: number; d: number; h: number;
  z0?: number;
  top: string; left: string; right: string;
  stroke?: string; sw?: number;
}> = ({ x, y, w, d, h, z0 = 0, top, left, right, stroke = '#2B2F36', sw = 1 }) => {
  const NN = pt(x, y, z0 + h);
  const EE = pt(x + w, y, z0 + h);
  const SS = pt(x + w, y + d, z0 + h);
  const WW = pt(x, y + d, z0 + h);
  const EEb = pt(x + w, y, z0);
  const SSb = pt(x + w, y + d, z0);
  const WWb = pt(x, y + d, z0);
  return (
    <g>
      <polygon points={`${WWb} ${SSb} ${SS} ${WW}`} fill={left}  stroke={stroke} strokeWidth={sw} strokeLinejoin="round" />
      <polygon points={`${EEb} ${SSb} ${SS} ${EE}`} fill={right} stroke={stroke} strokeWidth={sw} strokeLinejoin="round" />
      <polygon points={`${NN} ${EE} ${SS} ${WW}`}    fill={top}   stroke={stroke} strokeWidth={sw} strokeLinejoin="round" />
    </g>
  );
};

const PeakedRoof: React.FC<{
  x: number; y: number; w: number; d: number; baseH: number; peakH: number;
  topColor: string;
}> = ({ x, y, w, d, baseH, peakH, topColor }) => {
  const N = pt(x, y, baseH);
  const E = pt(x + w, y, baseH);
  const S = pt(x + w, y + d, baseH);
  const W = pt(x, y + d, baseH);
  const ridgeA = pt(x + w / 2, y, baseH + peakH);
  const ridgeB = pt(x + w / 2, y + d, baseH + peakH);

  return (
    <g>
      {/* left slope (south face, visible) */}
      <polygon points={`${W} ${S} ${ridgeB} ${ridgeA}`} fill={shade(topColor, -0.18)} stroke="#2B2F36" strokeWidth="1" strokeLinejoin="round" />
      {/* right slope (east face, visible) */}
      <polygon points={`${E} ${S} ${ridgeB} ${ridgeA}`} fill={shade(topColor, -0.08)} stroke="#2B2F36" strokeWidth="1" strokeLinejoin="round" />
    </g>
  );
};

const StripedRoof: React.FC<{
  x: number; y: number; w: number; d: number; baseH: number; peakH: number;
  stripeA: string; stripeB: string;
}> = ({ x, y, w, d, baseH, peakH, stripeA, stripeB }) => {
  const N = pt(x, y, baseH);
  const E = pt(x + w, y, baseH);
  const S = pt(x + w, y + d, baseH);
  const W = pt(x, y + d, baseH);
  const ridgeA = pt(x + w / 2, y, baseH + peakH);
  const ridgeB = pt(x + w / 2, y + d, baseH + peakH);

  const stripes = 6;
  const stripeWidth = w / stripes;
  const stripesEast: React.ReactElement[] = [];
  const stripesSouth: React.ReactElement[] = [];

  for (let i = 0; i < stripes; i++) {
    const u0 = i * stripeWidth;
    const u1 = (i + 1) * stripeWidth;
    const color = i % 2 === 0 ? stripeA : stripeB;
    // east face stripe (between (x+u, y+d, baseH) and ridge)
    const a = pt(x + u0, y + d, baseH);
    const b = pt(x + u1, y + d, baseH);
    const c = pt(x + u1, y + d / 2, baseH + peakH * ((u1 < w / 2) ? (u1 / (w / 2)) : ((w - u1) / (w / 2))));
    const d2 = pt(x + u0, y + d / 2, baseH + peakH * ((u0 < w / 2) ? (u0 / (w / 2)) : ((w - u0) / (w / 2))));
    stripesEast.push(<polygon key={`e${i}`} points={`${a} ${b} ${c} ${d2}`} fill={color} stroke="#2B2F36" strokeWidth="0.4" />);
  }

  return (
    <g>
      {/* full backdrop simpler: two big slope polygons split into stripes via gradient pattern */}
      <polygon points={`${W} ${S} ${ridgeB} ${ridgeA}`} fill={stripeB} stroke="#2B2F36" strokeWidth="1" strokeLinejoin="round" />
      <polygon points={`${E} ${S} ${ridgeB} ${ridgeA}`} fill={stripeA} stroke="#2B2F36" strokeWidth="1" strokeLinejoin="round" />
      {/* stripe overlays on south face */}
      <StripesOnFace x={x} y={y + d} w={w} h={peakH} baseH={baseH} stripeA={stripeA} stripeB={stripeB} axis="x" />
    </g>
  );
};

const StripesOnFace: React.FC<{
  x: number; y: number; w: number; h: number; baseH: number;
  stripeA: string; stripeB: string; axis: 'x' | 'y';
}> = ({ x, y, w, h, baseH, stripeA, stripeB, axis }) => {
  const stripes = 6;
  const elems: React.ReactElement[] = [];
  for (let i = 0; i < stripes; i++) {
    const u0 = (i / stripes) * w;
    const u1 = ((i + 1) / stripes) * w;
    const color = i % 2 === 0 ? stripeA : stripeB;
    if (axis === 'x') {
      // stripe runs vertically on south face
      const a = pt(x + u0, y, baseH);
      const b = pt(x + u1, y, baseH);
      const c = pt(x + u1, y, baseH + h);
      const d = pt(x + u0, y, baseH + h);
      elems.push(<polygon key={i} points={`${a} ${b} ${c} ${d}`} fill={color} stroke="#2B2F36" strokeWidth="0.3" />);
    }
  }
  return <g>{elems}</g>;
};

const WindowsOnFace: React.FC<{
  // face: 'right' = east face at x+w plane (varying y, z)
  // face: 'left' = south face at y+d plane (varying x, z)
  face: 'right' | 'left';
  x: number; y: number; w: number; d: number; h: number;
  rows: number; cols: number; color: string;
}> = ({ face, x, y, w, d, h, rows, cols, color }) => {
  const elems: React.ReactElement[] = [];
  const padU = 0.18;
  const padV = 0.22;
  const along = face === 'right' ? d : w;
  const cellW = (along - 2 * padU) / cols;
  const cellH = (h - 2 * padV) / rows;
  const winW = cellW * 0.55;
  const winH = cellH * 0.55;
  for (let i = 0; i < cols; i++) {
    for (let j = 0; j < rows; j++) {
      const u = padU + i * cellW + (cellW - winW) / 2;
      const v = padV + j * cellH + (cellH - winH) / 2;
      let p1: string, p2: string, p3: string, p4: string;
      if (face === 'right') {
        p1 = pt(x + w, y + u, v);
        p2 = pt(x + w, y + u + winW, v);
        p3 = pt(x + w, y + u + winW, v + winH);
        p4 = pt(x + w, y + u, v + winH);
      } else {
        p1 = pt(x + u, y + d, v);
        p2 = pt(x + u + winW, y + d, v);
        p3 = pt(x + u + winW, y + d, v + winH);
        p4 = pt(x + u, y + d, v + winH);
      }
      elems.push(
        <polygon
          key={`${face}-${i}-${j}`}
          points={`${p1} ${p2} ${p3} ${p4}`}
          fill={color}
          stroke="#2B2F36"
          strokeWidth="0.4"
        />
      );
    }
  }
  return <g>{elems}</g>;
};

const Awning: React.FC<{
  x: number; y: number; w: number; d: number; h: number; color: string;
  hasAI?: boolean;
}> = ({ x, y, w, d, h, color, hasAI }) => {
  /* small slab below windows on front faces */
  const ay = 0.55; // awning vertical position (fraction of h)
  const az = h * ay;
  const ah = 0.12; // awning thickness in z units
  const proj = 0.18; // how far it juts out

  /* south face awning - extends in +y direction by proj */
  const sA = pt(x, y + d, az);
  const sB = pt(x + w, y + d, az);
  const sC = pt(x + w, y + d + proj, az);
  const sD = pt(x, y + d + proj, az);
  const sE = pt(x, y + d, az - ah);
  const sF = pt(x + w, y + d, az - ah);
  const sG = pt(x + w, y + d + proj, az - ah);
  const sH = pt(x, y + d + proj, az - ah);

  /* east face awning - extends in +x direction */
  const eA = pt(x + w, y, az);
  const eB = pt(x + w, y + d, az);
  const eC = pt(x + w + proj, y + d, az);
  const eD = pt(x + w + proj, y, az);
  const eE = pt(x + w, y, az - ah);
  const eF = pt(x + w, y + d, az - ah);
  const eG = pt(x + w + proj, y + d, az - ah);
  const eH = pt(x + w + proj, y, az - ah);

  const dark = shade(color, -0.2);
  const light = shade(color, 0.08);
  return (
    <g className={hasAI ? 'awning-ai' : ''}>
      {/* south awning */}
      <polygon points={`${sD} ${sC} ${sG} ${sH}`} fill={dark} stroke="#2B2F36" strokeWidth="0.6" />
      <polygon points={`${sA} ${sB} ${sC} ${sD}`} fill={light} stroke="#2B2F36" strokeWidth="0.6" />
      {/* east awning */}
      <polygon points={`${eD} ${eC} ${eG} ${eH}`} fill={dark} stroke="#2B2F36" strokeWidth="0.6" />
      <polygon points={`${eA} ${eB} ${eC} ${eD}`} fill={light} stroke="#2B2F36" strokeWidth="0.6" />
    </g>
  );
};

const Door: React.FC<{
  face: 'right' | 'left';
  x: number; y: number; w: number; d: number; h: number;
  color: string;
}> = ({ face, x, y, w, d, h, color }) => {
  const along = face === 'right' ? d : w;
  const doorW = 0.45;
  const doorH = h * 0.42;
  const u = (along - doorW) / 2;
  let p1: string, p2: string, p3: string, p4: string;
  if (face === 'right') {
    p1 = pt(x + w, y + u, 0);
    p2 = pt(x + w, y + u + doorW, 0);
    p3 = pt(x + w, y + u + doorW, doorH);
    p4 = pt(x + w, y + u, doorH);
  } else {
    p1 = pt(x + u, y + d, 0);
    p2 = pt(x + u + doorW, y + d, 0);
    p3 = pt(x + u + doorW, y + d, doorH);
    p4 = pt(x + u, y + d, doorH);
  }
  return <polygon points={`${p1} ${p2} ${p3} ${p4}`} fill={color} stroke="#2B2F36" strokeWidth="0.6" />;
};

/* ---------------- AI building (special composite, bigger + fancier) ---------------- */
const AIBuilding: React.FC<{
  b: Building;
  hovered: boolean;
  onHover: () => void;
  onLeave: () => void;
}> = ({ b, hovered, onHover, onLeave }) => {
  const { x, y, w, d, h } = b;
  /* modern tiered massing: tall ground floor + glass mid + small penthouse */
  const t1H = h * 0.22;
  const t2H = h * 0.50;
  const t3H = h * 0.28;
  const t2In = 0.18;
  const t3In = 0.55;

  /* glass entrance dims on south face center */
  const doorW = 1.5;
  const doorH = t1H * 0.85;
  const doorX0 = x + (w - doorW) / 2;

  /* modern palette */
  const wallWhite = '#F5F6F8';
  const wallShade = '#DCDEE3';
  const wallDeep = '#BFC2C9';
  const glassDark = '#1B2A3E';
  const glassMid = '#2C415A';
  const glassLight = '#4A6789';
  const concretePier = '#9CA3AE';
  const accent = '#5B8DEF';

  return (
    <g
      className={`shop shop-ai ${hovered ? 'is-hovered' : ''}`}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      style={{ cursor: 'pointer' }}
    >
      {/* ===== TIER 1: GROUND FLOOR — white concrete frame + full-height glass storefront ===== */}
      <IsoBox
        x={x} y={y} w={w} d={d} h={t1H}
        top={wallWhite} left={wallShade} right={wallDeep} sw={1}
      />
      {/* south-face storefront glass */}
      <polygon
        points={`${pt(x + 0.3, y + d, t1H * 0.08)} ${pt(x + w - 0.3, y + d, t1H * 0.08)} ${pt(x + w - 0.3, y + d, t1H * 0.92)} ${pt(x + 0.3, y + d, t1H * 0.92)}`}
        fill={glassDark} stroke="#0B1320" strokeWidth="0.6"
      />
      <polygon
        points={`${pt(x + 0.4, y + d, t1H * 0.15)} ${pt(x + 1.5, y + d, t1H * 0.15)} ${pt(x + 1.5, y + d, t1H * 0.85)} ${pt(x + 0.4, y + d, t1H * 0.85)}`}
        fill={glassLight} opacity="0.35"
      />
      {Array.from({ length: 5 }).map((_, i) => {
        const u = x + 0.3 + ((w - 0.6) * (i + 1)) / 6;
        return (
          <line
            key={`m-s-${i}`}
            x1={iso(u, y + d, t1H * 0.08)[0]}
            y1={iso(u, y + d, t1H * 0.08)[1]}
            x2={iso(u, y + d, t1H * 0.92)[0]}
            y2={iso(u, y + d, t1H * 0.92)[1]}
            stroke="#FFFFFF" strokeWidth="0.4" opacity="0.8"
          />
        );
      })}

      {/* east-face storefront glass */}
      <polygon
        points={`${pt(x + w, y + 0.3, t1H * 0.08)} ${pt(x + w, y + d - 0.3, t1H * 0.08)} ${pt(x + w, y + d - 0.3, t1H * 0.92)} ${pt(x + w, y + 0.3, t1H * 0.92)}`}
        fill={glassMid} stroke="#0B1320" strokeWidth="0.6"
      />
      {Array.from({ length: 5 }).map((_, i) => {
        const v = y + 0.3 + ((d - 0.6) * (i + 1)) / 6;
        return (
          <line
            key={`m-e-${i}`}
            x1={iso(x + w, v, t1H * 0.08)[0]}
            y1={iso(x + w, v, t1H * 0.08)[1]}
            x2={iso(x + w, v, t1H * 0.92)[0]}
            y2={iso(x + w, v, t1H * 0.92)[1]}
            stroke="#FFFFFF" strokeWidth="0.4" opacity="0.7"
          />
        );
      })}

      {/* corner concrete piers */}
      <polygon
        points={`${pt(x, y + d, 0)} ${pt(x + 0.3, y + d, 0)} ${pt(x + 0.3, y + d, t1H)} ${pt(x, y + d, t1H)}`}
        fill={concretePier} stroke="#5F656E" strokeWidth="0.5"
      />
      <polygon
        points={`${pt(x + w - 0.3, y + d, 0)} ${pt(x + w, y + d, 0)} ${pt(x + w, y + d, t1H)} ${pt(x + w - 0.3, y + d, t1H)}`}
        fill={concretePier} stroke="#5F656E" strokeWidth="0.5"
      />
      <polygon
        points={`${pt(x + w, y, 0)} ${pt(x + w, y + 0.3, 0)} ${pt(x + w, y + 0.3, t1H)} ${pt(x + w, y, t1H)}`}
        fill={concretePier} stroke="#5F656E" strokeWidth="0.5"
      />
      <polygon
        points={`${pt(x + w, y + d - 0.3, 0)} ${pt(x + w, y + d, 0)} ${pt(x + w, y + d, t1H)} ${pt(x + w, y + d - 0.3, t1H)}`}
        fill={concretePier} stroke="#5F656E" strokeWidth="0.5"
      />

      {/* ===== ENTRANCE: large glass door + cantilevered canopy ===== */}
      <polygon
        points={`${pt(doorX0, y + d, 0)} ${pt(doorX0 + doorW, y + d, 0)} ${pt(doorX0 + doorW, y + d, doorH)} ${pt(doorX0, y + d, doorH)}`}
        fill="#0B1320" stroke="#000" strokeWidth="0.8"
      />
      <line
        x1={iso(doorX0 + doorW / 2, y + d, 0)[0]}
        y1={iso(doorX0 + doorW / 2, y + d, 0)[1]}
        x2={iso(doorX0 + doorW / 2, y + d, doorH)[0]}
        y2={iso(doorX0 + doorW / 2, y + d, doorH)[1]}
        stroke="#FFFFFF" strokeWidth="0.6" opacity="0.7"
      />
      <polygon
        points={`${pt(doorX0 + 0.1, y + d, 0.05)} ${pt(doorX0 + doorW * 0.4, y + d, 0.05)} ${pt(doorX0 + doorW * 0.4, y + d, doorH - 0.05)} ${pt(doorX0 + 0.1, y + d, doorH - 0.05)}`}
        fill={glassLight} opacity="0.5"
      />
      {/* cantilevered canopy slab over entrance */}
      <IsoBox
        x={doorX0 - 0.2} y={y + d} w={doorW + 0.4} d={0.5} h={0.08}
        z0={t1H * 0.95}
        top={wallWhite} left={wallShade} right={wallDeep} sw={0.6}
      />
      <polygon
        points={`${pt(doorX0 - 0.2, y + d + 0.5, t1H * 0.95)} ${pt(doorX0 + doorW + 0.2, y + d + 0.5, t1H * 0.95)} ${pt(doorX0 + doorW + 0.2, y + d + 0.5, t1H * 0.95 + 0.04)} ${pt(doorX0 - 0.2, y + d + 0.5, t1H * 0.95 + 0.04)}`}
        fill={accent} opacity="0.85"
      />

      {/* ===== TIER 2: mid-rise glass curtain wall (3 floors) ===== */}
      <IsoBox
        x={x + t2In} y={y + t2In} w={w - t2In * 2} d={d - t2In * 2} h={t2H}
        z0={t1H}
        top={wallWhite} left={wallWhite} right={wallShade} sw={0.9}
      />
      {/* south-face curtain wall */}
      <polygon
        points={`${pt(x + t2In + 0.06, y + d - t2In, t1H + 0.05)} ${pt(x + w - t2In - 0.06, y + d - t2In, t1H + 0.05)} ${pt(x + w - t2In - 0.06, y + d - t2In, t1H + t2H - 0.05)} ${pt(x + t2In + 0.06, y + d - t2In, t1H + t2H - 0.05)}`}
        fill={glassDark} stroke="#0B1320" strokeWidth="0.5"
      />
      {/* horizontal floor strips on south face */}
      {[1, 2].map((i) => {
        const fz = t1H + (t2H * i) / 3;
        return (
          <polygon
            key={`fl-s-${i}`}
            points={`${pt(x + t2In + 0.06, y + d - t2In, fz - 0.03)} ${pt(x + w - t2In - 0.06, y + d - t2In, fz - 0.03)} ${pt(x + w - t2In - 0.06, y + d - t2In, fz + 0.03)} ${pt(x + t2In + 0.06, y + d - t2In, fz + 0.03)}`}
            fill={wallWhite} stroke="#9095A0" strokeWidth="0.3"
          />
        );
      })}
      {/* vertical fins on south curtain wall */}
      {Array.from({ length: 5 }).map((_, i) => {
        const u = x + t2In + 0.06 + ((w - t2In * 2 - 0.12) * (i + 1)) / 6;
        return (
          <line
            key={`mul-s-${i}`}
            x1={iso(u, y + d - t2In, t1H + 0.05)[0]}
            y1={iso(u, y + d - t2In, t1H + 0.05)[1]}
            x2={iso(u, y + d - t2In, t1H + t2H - 0.05)[0]}
            y2={iso(u, y + d - t2In, t1H + t2H - 0.05)[1]}
            stroke="#FFFFFF" strokeWidth="0.5" opacity="0.85"
          />
        );
      })}

      {/* east-face curtain wall */}
      <polygon
        points={`${pt(x + w - t2In, y + t2In + 0.06, t1H + 0.05)} ${pt(x + w - t2In, y + d - t2In - 0.06, t1H + 0.05)} ${pt(x + w - t2In, y + d - t2In - 0.06, t1H + t2H - 0.05)} ${pt(x + w - t2In, y + t2In + 0.06, t1H + t2H - 0.05)}`}
        fill={glassMid} stroke="#0B1320" strokeWidth="0.5"
      />
      {[1, 2].map((i) => {
        const fz = t1H + (t2H * i) / 3;
        return (
          <polygon
            key={`fl-e-${i}`}
            points={`${pt(x + w - t2In, y + t2In + 0.06, fz - 0.03)} ${pt(x + w - t2In, y + d - t2In - 0.06, fz - 0.03)} ${pt(x + w - t2In, y + d - t2In - 0.06, fz + 0.03)} ${pt(x + w - t2In, y + t2In + 0.06, fz + 0.03)}`}
            fill={wallWhite} stroke="#9095A0" strokeWidth="0.3"
          />
        );
      })}
      {Array.from({ length: 5 }).map((_, i) => {
        const v = y + t2In + 0.06 + ((d - t2In * 2 - 0.12) * (i + 1)) / 6;
        return (
          <line
            key={`mul-e-${i}`}
            x1={iso(x + w - t2In, v, t1H + 0.05)[0]}
            y1={iso(x + w - t2In, v, t1H + 0.05)[1]}
            x2={iso(x + w - t2In, v, t1H + t2H - 0.05)[0]}
            y2={iso(x + w - t2In, v, t1H + t2H - 0.05)[1]}
            stroke="#FFFFFF" strokeWidth="0.5" opacity="0.85"
          />
        );
      })}

      {/* brand signage stripe near top of tier 2 with LED dots */}
      <polygon
        points={`${pt(x + t2In + 0.8, y + d - t2In, t1H + t2H - 0.3)} ${pt(x + w - t2In - 0.8, y + d - t2In, t1H + t2H - 0.3)} ${pt(x + w - t2In - 0.8, y + d - t2In, t1H + t2H - 0.15)} ${pt(x + t2In + 0.8, y + d - t2In, t1H + t2H - 0.15)}`}
        fill="#0F172A" stroke={accent} strokeWidth="0.5"
      />
      {Array.from({ length: 8 }).map((_, i) => {
        const u = x + t2In + 0.85 + (((w - t2In * 2) - 1.7) * (i + 0.5)) / 8;
        const cx = iso(u, y + d - t2In, t1H + t2H - 0.22)[0];
        const cy = iso(u, y + d - t2In, t1H + t2H - 0.22)[1];
        return (
          <circle
            key={`sig-led-${i}`}
            cx={cx} cy={cy} r="1.1"
            fill={accent}
            className="led-dot"
            style={{ animationDelay: `${i * 0.15}s` }}
          />
        );
      })}

      {/* ===== SKY TERRACE (green roof on tier 2 setback) ===== */}
      <polygon
        points={`${pt(x + t2In, y + t2In, t1H + t2H)} ${pt(x + w - t2In, y + t2In, t1H + t2H)} ${pt(x + w - t2In, y + d - t2In, t1H + t2H)} ${pt(x + t2In, y + d - t2In, t1H + t2H)}`}
        fill="#7CB069" stroke="#4F7B3E" strokeWidth="0.5"
      />
      {[
        [t2In + 0.15, t2In + 0.15],
        [t2In + 0.15, d - t2In - 0.33],
        [w - t2In - 0.33, t2In + 0.15],
        [w - t2In - 0.33, d - t2In - 0.33],
      ].map(([dx, dy], i) => (
        <polygon
          key={`grn-${i}`}
          points={`${pt(x + dx, y + dy, t1H + t2H + 0.005)} ${pt(x + dx + 0.18, y + dy, t1H + t2H + 0.005)} ${pt(x + dx + 0.18, y + dy + 0.18, t1H + t2H + 0.005)} ${pt(x + dx, y + dy + 0.18, t1H + t2H + 0.005)}`}
          fill="#5E8B4A" stroke="#3E6334" strokeWidth="0.3"
        />
      ))}

      {/* ===== TIER 3: penthouse (glass cube) ===== */}
      <IsoBox
        x={x + t3In} y={y + t3In} w={w - t3In * 2} d={d - t3In * 2} h={t3H}
        z0={t1H + t2H}
        top={wallWhite} left={wallWhite} right={wallShade} sw={0.9}
      />
      <polygon
        points={`${pt(x + t3In + 0.06, y + d - t3In, t1H + t2H + 0.05)} ${pt(x + w - t3In - 0.06, y + d - t3In, t1H + t2H + 0.05)} ${pt(x + w - t3In - 0.06, y + d - t3In, t1H + t2H + t3H - 0.08)} ${pt(x + t3In + 0.06, y + d - t3In, t1H + t2H + t3H - 0.08)}`}
        fill={glassDark} stroke="#0B1320" strokeWidth="0.4"
      />
      <polygon
        points={`${pt(x + w - t3In, y + t3In + 0.06, t1H + t2H + 0.05)} ${pt(x + w - t3In, y + d - t3In - 0.06, t1H + t2H + 0.05)} ${pt(x + w - t3In, y + d - t3In - 0.06, t1H + t2H + t3H - 0.08)} ${pt(x + w - t3In, y + t3In + 0.06, t1H + t2H + t3H - 0.08)}`}
        fill={glassMid} stroke="#0B1320" strokeWidth="0.4"
      />
      {Array.from({ length: 3 }).map((_, i) => {
        const u = x + t3In + 0.06 + ((w - t3In * 2 - 0.12) * (i + 1)) / 4;
        return (
          <line
            key={`mul-p-s-${i}`}
            x1={iso(u, y + d - t3In, t1H + t2H + 0.05)[0]}
            y1={iso(u, y + d - t3In, t1H + t2H + 0.05)[1]}
            x2={iso(u, y + d - t3In, t1H + t2H + t3H - 0.08)[0]}
            y2={iso(u, y + d - t3In, t1H + t2H + t3H - 0.08)[1]}
            stroke="#FFFFFF" strokeWidth="0.4" opacity="0.8"
          />
        );
      })}

      {/* solar panel array on penthouse roof */}
      {[0, 1].map((row) =>
        [0, 1, 2].map((col) => (
          <IsoBox
            key={`sp-${row}-${col}`}
            x={x + t3In + 0.18 + col * 0.42}
            y={y + t3In + 0.18 + row * 0.42}
            w={0.32} d={0.32} h={0.06}
            z0={t1H + t2H + t3H}
            top="#1A2B3E" left="#10202E" right="#0A1623" sw={0.3}
          />
        ))
      )}
      {[0, 1].map((row) =>
        [0, 1, 2].map((col) => {
          const px = x + t3In + 0.18 + col * 0.42;
          const py = y + t3In + 0.18 + row * 0.42;
          return (
            <line
              key={`spl-${row}-${col}`}
              x1={iso(px + 0.16, py, t1H + t2H + t3H + 0.06)[0]}
              y1={iso(px + 0.16, py, t1H + t2H + t3H + 0.06)[1]}
              x2={iso(px + 0.16, py + 0.32, t1H + t2H + t3H + 0.06)[0]}
              y2={iso(px + 0.16, py + 0.32, t1H + t2H + t3H + 0.06)[1]}
              stroke="#3E5670" strokeWidth="0.4" opacity="0.7"
            />
          );
        })
      )}

      {/* HVAC unit on penthouse roof */}
      <IsoBox
        x={x + w - t3In - 0.5} y={y + t3In + 0.18} w={0.35} d={0.55} h={0.18}
        z0={t1H + t2H + t3H}
        top="#9CA3AE" left="#7B828D" right="#646A75" sw={0.4}
      />


      {/* tall antenna with signal rings */}
      <line
        x1={iso(x + w / 2, y + d / 2, t1H + t2H + t3H)[0]}
        y1={iso(x + w / 2, y + d / 2, t1H + t2H + t3H)[1]}
        x2={iso(x + w / 2, y + d / 2, t1H + t2H + t3H + 1.2)[0]}
        y2={iso(x + w / 2, y + d / 2, t1H + t2H + t3H + 1.2)[1]}
        stroke="#1E3A8A" strokeWidth="2.2"
      />
      <circle
        cx={iso(x + w / 2, y + d / 2, t1H + t2H + t3H + 1.28)[0]}
        cy={iso(x + w / 2, y + d / 2, t1H + t2H + t3H + 1.28)[1]}
        r="4.5" fill="#5B8DEF" stroke="#1E3A8A" strokeWidth="1" className="pulse-dot"
      />
      {/* signal rings expanding outward from antenna tip */}
      {[0, 1, 2].map((i) => (
        <circle
          key={`ring-${i}`}
          cx={iso(x + w / 2, y + d / 2, t1H + t2H + t3H + 1.28)[0]}
          cy={iso(x + w / 2, y + d / 2, t1H + t2H + t3H + 1.28)[1]}
          r="6" fill="none" stroke="#5B8DEF" strokeWidth="1.4"
          className={`signal-ring sr-${i}`}
          style={{ animationDelay: `${i * 0.8}s` }}
        />
      ))}

      {/* entrance steps (two flat slabs in front of door) */}
      <IsoBox
        x={doorX0 - 0.05} y={y + d} w={doorW + 0.1} d={0.18} h={0.06}
        top="#D8DCE3" left="#B9BFC8" right="#A4ABB6" sw={0.4}
      />
      <IsoBox
        x={doorX0} y={y + d + 0.18} w={doorW} d={0.18} h={0.12}
        top="#E8ECF2" left="#C7CDD5" right="#B1B7BF" sw={0.4}
      />
    </g>
  );
};

/* ---------------- building composite ---------------- */
const BuildingShape: React.FC<{
  b: Building;
  hovered: boolean;
  onHover: () => void;
  onLeave: () => void;
}> = ({ b, hovered, onHover, onLeave }) => {
  const isFlat = b.h < 0.2;
  return (
    <g
      className={`shop ${b.hasAI ? 'shop-ai' : ''} ${hovered ? 'is-hovered' : ''}`}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      style={{ cursor: isFlat ? 'default' : 'pointer' }}
    >
      <IsoBox x={b.x} y={b.y} w={b.w} d={b.d} h={b.h} top={b.top} left={b.left} right={b.right} />

      {!isFlat && b.roof === 'peaked' && (
        <PeakedRoof x={b.x} y={b.y} w={b.w} d={b.d} baseH={b.h} peakH={0.55} topColor={b.left} />
      )}
      {!isFlat && b.roof === 'striped' && b.stripeA && b.stripeB && (
        <StripedRoof x={b.x} y={b.y} w={b.w} d={b.d} baseH={b.h} peakH={0.55} stripeA={b.stripeA} stripeB={b.stripeB} />
      )}

      {!isFlat && b.windows && b.windows > 0 && (
        <>
          <WindowsOnFace face="right" x={b.x} y={b.y} w={b.w} d={b.d} h={b.h * 0.78}
            rows={b.h > 2.5 ? 3 : 1} cols={Math.max(1, b.windows - 1)}
            color={b.hasAI ? '#BFE0FF' : '#A9C5DD'} />
          <WindowsOnFace face="left" x={b.x} y={b.y} w={b.w} d={b.d} h={b.h * 0.78}
            rows={b.h > 2.5 ? 3 : 1} cols={Math.max(1, b.windows - 1)}
            color={b.hasAI ? '#BFE0FF' : '#A9C5DD'} />
        </>
      )}

      {!isFlat && b.awning && (
        <Awning x={b.x} y={b.y} w={b.w} d={b.d} h={b.h} color={b.awning} hasAI={b.hasAI} />
      )}

      {!isFlat && (
        <>
          <Door face="left"  x={b.x} y={b.y} w={b.w} d={b.d} h={b.h} color={b.hasAI ? '#1E3A8A' : '#3F4750'} />
          <Door face="right" x={b.x} y={b.y} w={b.w} d={b.d} h={b.h} color={b.hasAI ? '#1E3A8A' : '#3F4750'} />
        </>
      )}

      {/* AI antenna */}
      {b.hasAI && (
        <g>
          <line
            x1={iso(b.x + b.w / 2, b.y + b.d / 2, b.h)[0]}
            y1={iso(b.x + b.w / 2, b.y + b.d / 2, b.h)[1]}
            x2={iso(b.x + b.w / 2, b.y + b.d / 2, b.h)[0]}
            y2={iso(b.x + b.w / 2, b.y + b.d / 2, b.h)[1] - 28}
            stroke="#1E3A8A"
            strokeWidth="1.6"
          />
          <circle
            cx={iso(b.x + b.w / 2, b.y + b.d / 2, b.h)[0]}
            cy={iso(b.x + b.w / 2, b.y + b.d / 2, b.h)[1] - 30}
            r="3.5"
            fill="#5B8DEF"
            className="pulse-dot"
          />
        </g>
      )}
    </g>
  );
};

/* ---------------- plaza ---------------- */
const PlazaScene: React.FC = () => {
  const x0 = 9;
  const y0 = 12.2;
  const w = 6.5;
  const d = 2.6;
  const baseZ = 0.05;
  const fcx = x0 + w / 2;
  const fcy = y0 + d / 2;

  /* tile grid */
  const cols = 6;
  const rows = 3;
  const tw = w / cols;
  const td = d / rows;
  const tiles: React.ReactElement[] = [];
  for (let i = 0; i < cols; i++) {
    for (let j = 0; j < rows; j++) {
      const tx = x0 + i * tw;
      const ty = y0 + j * td;
      const light = (i + j) % 2 === 0;
      tiles.push(
        <polygon
          key={`tl-${i}-${j}`}
          points={`${pt(tx, ty, baseZ)} ${pt(tx + tw, ty, baseZ)} ${pt(tx + tw, ty + td, baseZ)} ${pt(tx, ty + td, baseZ)}`}
          fill={light ? '#EDE7DB' : '#D8D2C5'}
          stroke="#B8B2A6"
          strokeWidth="0.3"
        />
      );
    }
  }

  /* fountain — octagonal pool with raised rim + water in middle */
  const fountainPoly = (r: number, z: number) => {
    const pts: string[] = [];
    const sides = 12;
    for (let i = 0; i < sides; i++) {
      const a = (i / sides) * 2 * Math.PI;
      pts.push(pt(fcx + r * Math.cos(a), fcy + r * Math.sin(a), z));
    }
    return pts.join(' ');
  };

  return (
    <g>
      {/* base */}
      <polygon
        points={`${pt(x0, y0, 0)} ${pt(x0 + w, y0, 0)} ${pt(x0 + w, y0 + d, 0)} ${pt(x0, y0 + d, 0)}`}
        fill="#D2CBBD"
        stroke="#A8A294"
        strokeWidth="1"
      />
      {/* side wall (left face from base to baseZ) */}
      <polygon
        points={`${pt(x0, y0 + d, 0)} ${pt(x0 + w, y0 + d, 0)} ${pt(x0 + w, y0 + d, baseZ)} ${pt(x0, y0 + d, baseZ)}`}
        fill="#B5AE9F"
        stroke="#928B7E"
        strokeWidth="0.6"
      />
      <polygon
        points={`${pt(x0 + w, y0, 0)} ${pt(x0 + w, y0 + d, 0)} ${pt(x0 + w, y0 + d, baseZ)} ${pt(x0 + w, y0, baseZ)}`}
        fill="#9F9889"
        stroke="#7F7969"
        strokeWidth="0.6"
      />

      {/* tile grid on top */}
      {tiles}

      {/* central paved circle */}
      <polygon points={fountainPoly(0.9, baseZ + 0.001)} fill="#C2BBAB" stroke="#9A9485" strokeWidth="0.5" />

      {/* fountain pool rim (raised) */}
      <polygon points={fountainPoly(0.55, baseZ + 0.08)} fill="#9CADBA" stroke="#5F6D78" strokeWidth="0.7" />
      {/* fountain water (lower, inside rim) */}
      <polygon points={fountainPoly(0.42, baseZ + 0.07)} fill="#7BB3D1" stroke="#4E8AAA" strokeWidth="0.5" />
      <polygon points={fountainPoly(0.32, baseZ + 0.07)} fill="#9FCAE0" stroke="#5E9CBD" strokeWidth="0.3" opacity="0.6" />
      {/* water jet */}
      <line
        x1={iso(fcx, fcy, baseZ + 0.08)[0]}
        y1={iso(fcx, fcy, baseZ + 0.08)[1]}
        x2={iso(fcx, fcy, baseZ + 0.55)[0]}
        y2={iso(fcx, fcy, baseZ + 0.55)[1]}
        stroke="#B8DCEC"
        strokeWidth="2"
        strokeLinecap="round"
        className="jet"
      />
      <circle
        cx={iso(fcx, fcy, baseZ + 0.6)[0]}
        cy={iso(fcx, fcy, baseZ + 0.6)[1]}
        r="3.5"
        fill="#B8DCEC"
        className="jet-top"
      />

      {/* benches around fountain (4 short low boxes facing fountain) */}
      <IsoBox x={fcx - 0.9}  y={fcy - 1.0} w={0.7} d={0.18} h={0.15} z0={baseZ + 0.001}
        top="#8D6E54" left="#6E5440" right="#523F30" sw={0.5} />
      <IsoBox x={fcx + 0.2}  y={fcy + 0.7}  w={0.7} d={0.18} h={0.15} z0={baseZ + 0.001}
        top="#8D6E54" left="#6E5440" right="#523F30" sw={0.5} />
      <IsoBox x={fcx + 0.8}  y={fcy - 0.8} w={0.18} d={0.7} h={0.15} z0={baseZ + 0.001}
        top="#8D6E54" left="#6E5440" right="#523F30" sw={0.5} />
      <IsoBox x={fcx - 1.0}  y={fcy + 0.1} w={0.18} d={0.7} h={0.15} z0={baseZ + 0.001}
        top="#8D6E54" left="#6E5440" right="#523F30" sw={0.5} />

      {/* lamp posts: 2 thin columns with light orb on top */}
      {[
        [x0 + 0.4, y0 + 0.4],
        [x0 + w - 0.4, y0 + d - 0.4],
        [x0 + w - 0.4, y0 + 0.4],
        [x0 + 0.4, y0 + d - 0.4],
      ].map(([lx, ly], i) => {
        const baseTop = iso(lx, ly, baseZ + 0.0)[0];
        const baseTopY = iso(lx, ly, baseZ + 0.0)[1];
        const topX = iso(lx, ly, baseZ + 1.1)[0];
        const topY = iso(lx, ly, baseZ + 1.1)[1];
        return (
          <g key={`lamp-${i}`}>
            <IsoBox x={lx - 0.06} y={ly - 0.06} w={0.12} d={0.12} h={0.12} z0={baseZ + 0.001}
              top="#3A3F47" left="#2A2F36" right="#1F252B" sw={0.4} />
            <line x1={baseTop} y1={baseTopY} x2={topX} y2={topY} stroke="#3A3F47" strokeWidth="1.4" strokeLinecap="round" />
            <circle cx={topX} cy={topY - 4} r="3.5" fill="#FFE08A" stroke="#1F252B" strokeWidth="0.6" className="lamp-glow" />
          </g>
        );
      })}

      {/* planters (corners with small bushes) */}
      {[
        [x0 + 0.35, y0 + d - 0.35],
        [x0 + w - 0.35, y0 + 0.35],
      ].map(([px, py], i) => (
        <g key={`pl-${i}`}>
          <IsoBox x={px - 0.18} y={py - 0.18} w={0.36} d={0.36} h={0.18} z0={baseZ + 0.001}
            top="#A88062" left="#7E5E45" right="#5C4530" sw={0.5} />
          <ellipse
            cx={iso(px, py, baseZ + 0.2)[0]}
            cy={iso(px, py, baseZ + 0.2)[1] - 6}
            rx="10" ry="7"
            fill="#6FA058" stroke="#3F6533" strokeWidth="0.6"
          />
          <ellipse
            cx={iso(px - 0.1, py, baseZ + 0.2)[0]}
            cy={iso(px - 0.1, py, baseZ + 0.2)[1] - 4}
            rx="6" ry="5"
            fill="#82B770" stroke="#3F6533" strokeWidth="0.4"
          />
        </g>
      ))}
    </g>
  );
};

/* ---------------- tree ---------------- */
const Tree: React.FC<{ x: number; y: number; size?: number }> = ({ x, y, size = 1 }) => {
  const [tx, ty] = iso(x, y, 0);
  return (
    <g transform={`translate(${tx}, ${ty})`} className="tree-sway">
      <ellipse cx="0" cy="-2" rx={2.4 * size} ry={6 * size} fill="#5E4E33" />
      <ellipse cx="0" cy={-16 * size} rx={11 * size} ry={9 * size} fill="#6FA058" stroke="#2B2F36" strokeWidth="0.8" />
      <ellipse cx={-6 * size} cy={-12 * size} rx={7 * size} ry={6 * size} fill="#7CB069" stroke="#2B2F36" strokeWidth="0.6" />
      <ellipse cx={6 * size} cy={-14 * size} rx={6 * size} ry={5.5 * size} fill="#82B770" stroke="#2B2F36" strokeWidth="0.6" />
    </g>
  );
};

/* ---------------- car (realistic-looking sedan) ---------------- */
const Car: React.FC<{ x: number; y: number; color: string; dir?: 'h' | 'v' }> = ({ x, y, color, dir = 'h' }) => {
  const isH = dir === 'h';
  const L = 1.35;
  const W = 0.62;
  const w = isH ? L : W;
  const d = isH ? W : L;

  /* wheels */
  const wheelS = 0.22;
  const wheelH = 0.18;
  const wheelInset = 0.06;
  const wheelPositions: Array<[number, number]> = isH
    ? [
        [x + wheelInset,              y - 0.025],
        [x + w - wheelInset - wheelS, y - 0.025],
        [x + wheelInset,              y + d + 0.025 - wheelS],
        [x + w - wheelInset - wheelS, y + d + 0.025 - wheelS],
      ]
    : [
        [x - 0.025,              y + wheelInset],
        [x + w + 0.025 - wheelS, y + wheelInset],
        [x - 0.025,              y + d - wheelInset - wheelS],
        [x + w + 0.025 - wheelS, y + d - wheelInset - wheelS],
      ];

  /* body geometry */
  const bodyZ0 = wheelH * 0.42;
  const chassisH = 0.28;
  const cabH = 0.3;

  /* cab dimensions */
  const cabFracLong = 0.5;
  const cabFracWide = 0.78;
  const cabL = L * cabFracLong;
  const cabSide = W * cabFracWide;
  const cabRearGap = L * 0.2;
  const cabX = isH ? x + cabRearGap : x + (W - cabSide) / 2;
  const cabY = isH ? y + (W - cabSide) / 2 : y + cabRearGap;
  const cabW = isH ? cabL : cabSide;
  const cabD = isH ? cabSide : cabL;

  /* colors */
  const bTop = shade(color, 0.16);
  const bLeft = shade(color, -0.02);
  const bRight = shade(color, -0.14);
  const bumperColor = shade(color, -0.35);
  const wheelArchColor = shade(color, -0.45);
  const winTop = '#0E1A2A';
  const winLeft = '#1F2F44';
  const winRight = '#152232';

  const [shCx, shCy] = iso(x + w / 2, y + d / 2, 0);

  /* axis-aware fronts/backs */
  const frontMin = isH ? y + 0.08 : x + 0.08;
  const frontMax = isH ? y + d - 0.08 : x + w - 0.08;

  return (
    <g>
      {/* drop shadow */}
      <ellipse cx={shCx} cy={shCy + 4} rx={isH ? 28 : 14} ry={isH ? 10 : 20} fill="#000" opacity="0.25" />

      {/* wheel arches (dark ovals behind wheels for "well" look) */}
      {wheelPositions.map(([wx, wy], i) => {
        const ax = wx + wheelS / 2;
        const ay = wy + wheelS / 2;
        const [ix, iy] = iso(ax, ay, bodyZ0 + 0.04);
        return (
          <ellipse
            key={`arch-${i}`}
            cx={ix} cy={iy}
            rx={isH ? 8 : 5}
            ry={isH ? 4 : 7}
            fill={wheelArchColor}
            opacity="0.85"
          />
        );
      })}

      {/* wheels (tires) */}
      {wheelPositions.map(([wx, wy], i) => (
        <g key={`wh-${i}`}>
          <IsoBox
            x={wx} y={wy} w={wheelS} d={wheelS} h={wheelH}
            top="#1F2329" left="#13171C" right="#0A0D11" sw={0.4}
          />
          {/* rim accent */}
          <circle
            cx={iso(wx + wheelS / 2, wy + wheelS / 2, wheelH)[0]}
            cy={iso(wx + wheelS / 2, wy + wheelS / 2, wheelH)[1]}
            r="1.6"
            fill="#6B7178"
          />
        </g>
      ))}

      {/* chassis (lower body — main car color) */}
      <IsoBox
        x={x} y={y} w={w} d={d} h={chassisH} z0={bodyZ0}
        top={bTop} left={bLeft} right={bRight} sw={0.7}
      />

      {/* bumpers — dark band at front + back covering lower portion of chassis */}
      {isH ? (
        <>
          {/* front bumper (east face full + small wrap on south face) */}
          <polygon
            points={`${pt(x + w, y + 0.04, bodyZ0)} ${pt(x + w, y + d - 0.04, bodyZ0)} ${pt(x + w, y + d - 0.04, bodyZ0 + chassisH * 0.32)} ${pt(x + w, y + 0.04, bodyZ0 + chassisH * 0.32)}`}
            fill={bumperColor} stroke="#1F2329" strokeWidth="0.4"
          />
          {/* rear bumper (west face) */}
          <polygon
            points={`${pt(x, y + 0.04, bodyZ0)} ${pt(x, y + d - 0.04, bodyZ0)} ${pt(x, y + d - 0.04, bodyZ0 + chassisH * 0.32)} ${pt(x, y + 0.04, bodyZ0 + chassisH * 0.32)}`}
            fill={bumperColor} stroke="#1F2329" strokeWidth="0.4"
          />
          {/* side-skirt accent strip on south face */}
          <polygon
            points={`${pt(x + 0.06, y + d, bodyZ0)} ${pt(x + w - 0.06, y + d, bodyZ0)} ${pt(x + w - 0.06, y + d, bodyZ0 + chassisH * 0.18)} ${pt(x + 0.06, y + d, bodyZ0 + chassisH * 0.18)}`}
            fill={bumperColor} stroke="none"
          />
        </>
      ) : (
        <>
          <polygon
            points={`${pt(x + 0.04, y + d, bodyZ0)} ${pt(x + w - 0.04, y + d, bodyZ0)} ${pt(x + w - 0.04, y + d, bodyZ0 + chassisH * 0.32)} ${pt(x + 0.04, y + d, bodyZ0 + chassisH * 0.32)}`}
            fill={bumperColor} stroke="#1F2329" strokeWidth="0.4"
          />
          <polygon
            points={`${pt(x + 0.04, y, bodyZ0)} ${pt(x + w - 0.04, y, bodyZ0)} ${pt(x + w - 0.04, y, bodyZ0 + chassisH * 0.32)} ${pt(x + 0.04, y, bodyZ0 + chassisH * 0.32)}`}
            fill={bumperColor} stroke="#1F2329" strokeWidth="0.4"
          />
        </>
      )}

      {/* hood/trunk highlight band on chassis top — gives shine */}
      <polygon
        points={`${pt(x + 0.05, y + 0.05, bodyZ0 + chassisH)} ${pt(x + w - 0.05, y + 0.05, bodyZ0 + chassisH)} ${pt(x + w - 0.05, y + d - 0.05, bodyZ0 + chassisH)} ${pt(x + 0.05, y + d - 0.05, bodyZ0 + chassisH)}`}
        fill={shade(color, 0.28)}
        stroke="none"
        opacity="0.65"
      />

      {/* hood seam (line separating hood from windshield base) */}
      {isH ? (
        <line
          x1={iso(cabX + cabW, y + 0.05, bodyZ0 + chassisH)[0]}
          y1={iso(cabX + cabW, y + 0.05, bodyZ0 + chassisH)[1]}
          x2={iso(cabX + cabW, y + d - 0.05, bodyZ0 + chassisH)[0]}
          y2={iso(cabX + cabW, y + d - 0.05, bodyZ0 + chassisH)[1]}
          stroke={shade(color, -0.3)} strokeWidth="0.5"
        />
      ) : (
        <line
          x1={iso(x + 0.05, cabY + cabD, bodyZ0 + chassisH)[0]}
          y1={iso(x + 0.05, cabY + cabD, bodyZ0 + chassisH)[1]}
          x2={iso(x + w - 0.05, cabY + cabD, bodyZ0 + chassisH)[0]}
          y2={iso(x + w - 0.05, cabY + cabD, bodyZ0 + chassisH)[1]}
          stroke={shade(color, -0.3)} strokeWidth="0.5"
        />
      )}

      {/* cab (greenhouse) */}
      <IsoBox
        x={cabX} y={cabY} w={cabW} d={cabD} h={cabH}
        z0={bodyZ0 + chassisH}
        top={winTop} left={winLeft} right={winRight} sw={0.7}
      />

      {/* roof color overlay */}
      <polygon
        points={`${pt(cabX + 0.04, cabY + 0.04, bodyZ0 + chassisH + cabH)} ${pt(cabX + cabW - 0.04, cabY + 0.04, bodyZ0 + chassisH + cabH)} ${pt(cabX + cabW - 0.04, cabY + cabD - 0.04, bodyZ0 + chassisH + cabH)} ${pt(cabX + 0.04, cabY + cabD - 0.04, bodyZ0 + chassisH + cabH)}`}
        fill={shade(color, 0.08)}
        stroke="#2B2F36"
        strokeWidth="0.4"
      />

      {/* split side windows: door pillar (B-pillar) seam */}
      {isH ? (
        <line
          x1={iso(cabX + cabW * 0.5, cabY + cabD, bodyZ0 + chassisH + 0.04)[0]}
          y1={iso(cabX + cabW * 0.5, cabY + cabD, bodyZ0 + chassisH + 0.04)[1]}
          x2={iso(cabX + cabW * 0.5, cabY + cabD, bodyZ0 + chassisH + cabH - 0.04)[0]}
          y2={iso(cabX + cabW * 0.5, cabY + cabD, bodyZ0 + chassisH + cabH - 0.04)[1]}
          stroke={shade(color, -0.4)} strokeWidth="0.8"
        />
      ) : (
        <line
          x1={iso(x + w, cabY + cabD * 0.5, bodyZ0 + chassisH + 0.04)[0]}
          y1={iso(x + w, cabY + cabD * 0.5, bodyZ0 + chassisH + 0.04)[1]}
          x2={iso(x + w, cabY + cabD * 0.5, bodyZ0 + chassisH + cabH - 0.04)[0]}
          y2={iso(x + w, cabY + cabD * 0.5, bodyZ0 + chassisH + cabH - 0.04)[1]}
          stroke={shade(color, -0.4)} strokeWidth="0.8"
        />
      )}

      {/* window shine streaks (diagonal lighter slash) */}
      {isH && (
        <polygon
          points={`${pt(cabX + 0.06, cabY + cabD, bodyZ0 + chassisH + cabH * 0.55)} ${pt(cabX + cabW * 0.4, cabY + cabD, bodyZ0 + chassisH + cabH - 0.04)} ${pt(cabX + cabW * 0.42, cabY + cabD, bodyZ0 + chassisH + cabH - 0.04)} ${pt(cabX + 0.08, cabY + cabD, bodyZ0 + chassisH + cabH * 0.5)}`}
          fill="#5E7A96" opacity="0.45"
        />
      )}
      {!isH && (
        <polygon
          points={`${pt(x + w, cabY + 0.06, bodyZ0 + chassisH + cabH * 0.55)} ${pt(x + w, cabY + cabD * 0.4, bodyZ0 + chassisH + cabH - 0.04)} ${pt(x + w, cabY + cabD * 0.42, bodyZ0 + chassisH + cabH - 0.04)} ${pt(x + w, cabY + 0.08, bodyZ0 + chassisH + cabH * 0.5)}`}
          fill="#5E7A96" opacity="0.45"
        />
      )}

      {/* door handle (small dash on side face) */}
      {isH ? (
        <line
          x1={iso(cabX + cabW * 0.7, y + d, bodyZ0 + chassisH * 0.78)[0]}
          y1={iso(cabX + cabW * 0.7, y + d, bodyZ0 + chassisH * 0.78)[1]}
          x2={iso(cabX + cabW * 0.85, y + d, bodyZ0 + chassisH * 0.78)[0]}
          y2={iso(cabX + cabW * 0.85, y + d, bodyZ0 + chassisH * 0.78)[1]}
          stroke={shade(color, -0.4)} strokeWidth="1"
          strokeLinecap="round"
        />
      ) : (
        <line
          x1={iso(x + w, cabY + cabD * 0.7, bodyZ0 + chassisH * 0.78)[0]}
          y1={iso(x + w, cabY + cabD * 0.7, bodyZ0 + chassisH * 0.78)[1]}
          x2={iso(x + w, cabY + cabD * 0.85, bodyZ0 + chassisH * 0.78)[0]}
          y2={iso(x + w, cabY + cabD * 0.85, bodyZ0 + chassisH * 0.78)[1]}
          stroke={shade(color, -0.4)} strokeWidth="1"
          strokeLinecap="round"
        />
      )}

      {/* side mirrors (small protrusion at cab front-side corner) */}
      {isH && (
        <circle
          cx={iso(cabX + cabW - 0.04, y + d + 0.06, bodyZ0 + chassisH + cabH * 0.4)[0]}
          cy={iso(cabX + cabW - 0.04, y + d + 0.06, bodyZ0 + chassisH + cabH * 0.4)[1]}
          r="1.5" fill={bRight} stroke="#1F2329" strokeWidth="0.4"
        />
      )}
      {!isH && (
        <circle
          cx={iso(x + w + 0.06, cabY + cabD - 0.04, bodyZ0 + chassisH + cabH * 0.4)[0]}
          cy={iso(x + w + 0.06, cabY + cabD - 0.04, bodyZ0 + chassisH + cabH * 0.4)[1]}
          r="1.5" fill={bRight} stroke="#1F2329" strokeWidth="0.4"
        />
      )}

      {/* headlights (front) — 2 per side, brighter */}
      {isH && (
        <>
          <polygon
            points={`${pt(x + w, frontMin, bodyZ0 + chassisH * 0.42)} ${pt(x + w, frontMin + 0.13, bodyZ0 + chassisH * 0.42)} ${pt(x + w, frontMin + 0.13, bodyZ0 + chassisH * 0.72)} ${pt(x + w, frontMin, bodyZ0 + chassisH * 0.72)}`}
            fill="#FFF4C2" stroke="#1F2329" strokeWidth="0.3"
          />
          <polygon
            points={`${pt(x + w, frontMax - 0.13, bodyZ0 + chassisH * 0.42)} ${pt(x + w, frontMax, bodyZ0 + chassisH * 0.42)} ${pt(x + w, frontMax, bodyZ0 + chassisH * 0.72)} ${pt(x + w, frontMax - 0.13, bodyZ0 + chassisH * 0.72)}`}
            fill="#FFF4C2" stroke="#1F2329" strokeWidth="0.3"
          />
          {/* grille (dark horizontal stripe between headlights) */}
          <polygon
            points={`${pt(x + w, frontMin + 0.18, bodyZ0 + chassisH * 0.48)} ${pt(x + w, frontMax - 0.18, bodyZ0 + chassisH * 0.48)} ${pt(x + w, frontMax - 0.18, bodyZ0 + chassisH * 0.66)} ${pt(x + w, frontMin + 0.18, bodyZ0 + chassisH * 0.66)}`}
            fill="#1A1D22" stroke="#0E1115" strokeWidth="0.3"
          />
          {/* taillights — 2 per side, red */}
          <polygon
            points={`${pt(x, frontMin, bodyZ0 + chassisH * 0.45)} ${pt(x, frontMin + 0.13, bodyZ0 + chassisH * 0.45)} ${pt(x, frontMin + 0.13, bodyZ0 + chassisH * 0.7)} ${pt(x, frontMin, bodyZ0 + chassisH * 0.7)}`}
            fill="#D14B3D" stroke="#1F2329" strokeWidth="0.3"
          />
          <polygon
            points={`${pt(x, frontMax - 0.13, bodyZ0 + chassisH * 0.45)} ${pt(x, frontMax, bodyZ0 + chassisH * 0.45)} ${pt(x, frontMax, bodyZ0 + chassisH * 0.7)} ${pt(x, frontMax - 0.13, bodyZ0 + chassisH * 0.7)}`}
            fill="#D14B3D" stroke="#1F2329" strokeWidth="0.3"
          />
          {/* license plate */}
          <polygon
            points={`${pt(x, frontMin + 0.17, bodyZ0 + chassisH * 0.18)} ${pt(x, frontMax - 0.17, bodyZ0 + chassisH * 0.18)} ${pt(x, frontMax - 0.17, bodyZ0 + chassisH * 0.32)} ${pt(x, frontMin + 0.17, bodyZ0 + chassisH * 0.32)}`}
            fill="#F5F0DA" stroke="#1F2329" strokeWidth="0.3"
          />
        </>
      )}
      {!isH && (
        <>
          <polygon
            points={`${pt(frontMin, y + d, bodyZ0 + chassisH * 0.42)} ${pt(frontMin + 0.13, y + d, bodyZ0 + chassisH * 0.42)} ${pt(frontMin + 0.13, y + d, bodyZ0 + chassisH * 0.72)} ${pt(frontMin, y + d, bodyZ0 + chassisH * 0.72)}`}
            fill="#FFF4C2" stroke="#1F2329" strokeWidth="0.3"
          />
          <polygon
            points={`${pt(frontMax - 0.13, y + d, bodyZ0 + chassisH * 0.42)} ${pt(frontMax, y + d, bodyZ0 + chassisH * 0.42)} ${pt(frontMax, y + d, bodyZ0 + chassisH * 0.72)} ${pt(frontMax - 0.13, y + d, bodyZ0 + chassisH * 0.72)}`}
            fill="#FFF4C2" stroke="#1F2329" strokeWidth="0.3"
          />
          <polygon
            points={`${pt(frontMin + 0.18, y + d, bodyZ0 + chassisH * 0.48)} ${pt(frontMax - 0.18, y + d, bodyZ0 + chassisH * 0.48)} ${pt(frontMax - 0.18, y + d, bodyZ0 + chassisH * 0.66)} ${pt(frontMin + 0.18, y + d, bodyZ0 + chassisH * 0.66)}`}
            fill="#1A1D22" stroke="#0E1115" strokeWidth="0.3"
          />
          <polygon
            points={`${pt(frontMin, y, bodyZ0 + chassisH * 0.45)} ${pt(frontMin + 0.13, y, bodyZ0 + chassisH * 0.45)} ${pt(frontMin + 0.13, y, bodyZ0 + chassisH * 0.7)} ${pt(frontMin, y, bodyZ0 + chassisH * 0.7)}`}
            fill="#D14B3D" stroke="#1F2329" strokeWidth="0.3"
          />
          <polygon
            points={`${pt(frontMax - 0.13, y, bodyZ0 + chassisH * 0.45)} ${pt(frontMax, y, bodyZ0 + chassisH * 0.45)} ${pt(frontMax, y, bodyZ0 + chassisH * 0.7)} ${pt(frontMax - 0.13, y, bodyZ0 + chassisH * 0.7)}`}
            fill="#D14B3D" stroke="#1F2329" strokeWidth="0.3"
          />
        </>
      )}
    </g>
  );
};

/* ---------------- person ---------------- */
const Person: React.FC<{ x: number; y: number; color: string; bobDelay?: number }> = ({
  x, y, color, bobDelay = 0,
}) => {
  const [tx, ty] = iso(x, y, 0);
  return (
    <g transform={`translate(${tx}, ${ty})`}>
      <g className="bob" style={{ animationDelay: `${bobDelay}s` }}>
        <ellipse cx="0" cy="-3" rx="5" ry="2" fill="#000" opacity="0.18" />
        <rect x="-2.5" y="-12" width="5" height="9" fill={color} stroke="#2B2F36" strokeWidth="0.5" rx="1" />
        <circle cx="0" cy="-15" r="2.5" fill="#F2C9A7" stroke="#2B2F36" strokeWidth="0.5" />
      </g>
    </g>
  );
};

/* ---------------- walking person (animated path) ---------------- */
const Walker: React.FC<{ path: string; color: string; delay?: number; duration?: number }> = ({
  path, color, delay = 0, duration = 22,
}) => (
  <g style={{ offsetPath: `path('${path}')`, animation: `walkPath ${duration}s linear ${delay}s infinite` } as React.CSSProperties}>
    <g className="walker-fig">
      <ellipse cx="0" cy="-1" rx="5" ry="2" fill="#000" opacity="0.2" />
      <rect x="-2.5" y="-11" width="5" height="9" fill={color} stroke="#2B2F36" strokeWidth="0.5" rx="1" />
      <circle cx="0" cy="-14" r="2.5" fill="#F2C9A7" stroke="#2B2F36" strokeWidth="0.5" />
    </g>
  </g>
);

/* ---------------- color helper ---------------- */
function shade(hex: string, amount: number): string {
  const c = hex.replace('#', '');
  const num = parseInt(c, 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;
  r = Math.max(0, Math.min(255, Math.round(r + 255 * amount)));
  g = Math.max(0, Math.min(255, Math.round(g + 255 * amount)));
  b = Math.max(0, Math.min(255, Math.round(b + 255 * amount)));
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}

/* ---------------- main scene ---------------- */
const AICityScene: React.FC = () => {
  const [hovered, setHovered] = useState<string | null>(null);

  /* AI building lookup */
  const ai = BUILDINGS.find((b) => b.hasAI)!;
  const aiCenter = iso(ai.x + ai.w / 2, ai.y + ai.d / 2, 0);
  const aiTop = iso(ai.x + ai.w / 2, ai.y + ai.d / 2, ai.h);

  /* AI crowd - all on south side around entrance, away from east-side car alley */
  const sy = ai.y + ai.d; // south wall
  const doorCx = ai.x + ai.w / 2;
  const crowdSpots: Array<[number, number, string, number]> = [
    [doorCx - 0.15, sy + 0.20, '#E76F51', 0.0],
    [doorCx + 0.20, sy + 0.35, '#F4A261', 0.3],
    [doorCx - 0.35, sy + 0.55, '#2A9D8F', 0.6],
    [doorCx + 0.40, sy + 0.75, '#264653', 0.9],
    [doorCx - 0.60, sy + 0.95, '#8E6B7A', 0.2],
    [doorCx + 0.70, sy + 0.45, '#5B8DEF', 0.5],
    [doorCx - 1.10, sy + 0.65, '#A65A6B', 0.8],
    [doorCx + 1.20, sy + 0.85, '#E76F51', 1.1],
  ];

  /* sparse people near other buildings */
  const lonely: Array<[number, number, string]> = [
    [2.5, 2.9, '#6B7A8F'],
    [5.0, 2.9, '#8E6B7A'],
    [9.5, 3.0, '#4A6572'],
    [13.0, 9.3, '#7A8B6F'],
    [14.5, 9.3, '#8E6B7A'],
  ];

  /* tree spots */
  const treeSpots: Array<[number, number, number?]> = [
    [6.35, 0.2], [8.65, 0.2], [6.35, 5], [8.65, 5],
    [6.35, 10], [8.65, 10], [6.35, 14], [8.65, 14],
    [2, 6.35], [5, 6.35], [10, 6.35], [14, 6.35],
    [2, 8.65], [5, 8.65],
    [10, 12.5, 0.85], [12, 12.7, 0.85], [14, 12.6, 0.85],
  ];

  /* parked queue cars on curb fronting AI building (east side, between AI and vertical road) */
  const parkedCars: Array<{ x: number; y: number; color: string; dir: 'h' | 'v' }> = [
    { x: 5.45, y: 9.2,  color: '#577590', dir: 'v' },
    { x: 5.45, y: 11.0, color: '#E0A458', dir: 'v' },
    { x: 5.45, y: 12.8, color: '#A65A6B', dir: 'v' },
    { x: 5.45, y: 14.6, color: '#5B8DEF', dir: 'v' },
  ];

  /* moving cars - sync'd to traffic light. h-cars green 0-6s of 12s cycle, v-cars green 6-12s.
     East-bound role taken by turning car (turn-h-part → turn-v-part). */
  const movingCars: Array<{ x: number; y: number; color: string; dir: 'h' | 'v'; anim: string }> = [
    { x: 7.5,  y: 7.85, color: '#2A9D8F', dir: 'h', anim: 'c-3' }, // west-bound south lane (h-green)
    { x: 7.85, y: 7.5,  color: '#F4A261', dir: 'v', anim: 'c-4' }, // south-bound east lane (v-green)
    { x: 6.85, y: 7.5,  color: '#577590', dir: 'v', anim: 'c-5' }, // north-bound west lane (v-green)
  ];

  /* combine ALL 3D items, tag depth, sort back-to-front for painter's algorithm */
  type Item = { depth: number; key: string; el: React.ReactElement };
  const items: Item[] = [];

  /* depth strategy:
     - buildings: NEAR-corner (back face). Items south/east of building have higher depth = render later = on top
     - small items (people, trees, parked cars): position x+y
     - moving cars: manual low depth so south-row buildings (AI etc.) always cover them on overlap
  */
  BUILDINGS.forEach((b) => {
    items.push({
      depth: b.x + b.y,
      key: `b-${b.id}`,
      el: b.hasAI ? (
        <AIBuilding
          b={b}
          hovered={hovered === b.id}
          onHover={() => setHovered(b.id)}
          onLeave={() => setHovered(null)}
        />
      ) : (
        <BuildingShape
          b={b}
          hovered={hovered === b.id}
          onHover={() => setHovered(b.id)}
          onLeave={() => setHovered(null)}
        />
      ),
    });
  });

  /* plaza (SE quadrant, in front of SE storefronts) */
  items.push({
    depth: 9 + 12.2, // near corner of plaza
    key: 'plaza',
    el: <PlazaScene />,
  });

  treeSpots.forEach(([tx, ty, sz], i) => {
    items.push({
      depth: tx + ty + 0.3,
      key: `t-${i}`,
      el: <Tree x={tx} y={ty} size={sz} />,
    });
  });

  /* parked queue cars east of AI - natural depth from position puts them in front of AI's east face */
  parkedCars.forEach((c, i) => {
    items.push({
      depth: c.x + c.y + 0.3,
      key: `pc-${i}`,
      el: <Car x={c.x} y={c.y} color={c.color} dir={c.dir} />,
    });
  });

  /* moving cars: low depth so AI (near 11.1) covers them when animation passes through.
     Still > NW buildings (near 1-6) so cars render in front of NW houses. */
  movingCars.forEach((c) => {
    items.push({
      depth: 7,
      key: `mc-${c.anim}`,
      el: (
        <g className={c.anim}>
          <Car x={c.x} y={c.y} color={c.color} dir={c.dir} />
        </g>
      ),
    });
  });

  /* turning car: east-bound enters, stops at light, then turns right (south) and exits.
     Two shapes share same world base so fade-swap is seamless on screen. */
  items.push({
    depth: 7,
    key: 'turn-h',
    el: (
      <g className="turn-h-part">
        <Car x={7.5} y={6.85} color="#8E5A9E" dir="h" />
      </g>
    ),
  });
  items.push({
    depth: 7.001,
    key: 'turn-v',
    el: (
      <g className="turn-v-part">
        <Car x={7.5} y={6.85} color="#8E5A9E" dir="v" />
      </g>
    ),
  });

  lonely.forEach(([px, py, col], i) => {
    items.push({
      depth: px + py + 0.3,
      key: `l-${i}`,
      el: <Person x={px} y={py} color={col} bobDelay={i * 0.4} />,
    });
  });

  crowdSpots.forEach(([px, py, col, dly], i) => {
    items.push({
      depth: px + py + 0.3,
      key: `cw-${i}`,
      el: <Person x={px} y={py} color={col} bobDelay={dly} />,
    });
  });

  items.sort((a, b) => a.depth - b.depth);

  return (
    <div className="ai-city-wrap">
      <style>{css}</style>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="ai-city-inner"
      >
        <svg
          viewBox="0 0 980 600"
          xmlns="http://www.w3.org/2000/svg"
          className="ai-city-svg"
          role="img"
          aria-label="Isometric city scene where the AI-powered business draws all the customers"
        >
          <defs>
            <linearGradient id="bg" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#F4F8FF" />
              <stop offset="100%" stopColor="#E0EAF7" />
            </linearGradient>
            <linearGradient id="groundGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#EFF3EA" />
              <stop offset="100%" stopColor="#D9E0CF" />
            </linearGradient>
            <radialGradient id="aiGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#7AB8FF" stopOpacity="0.55" />
              <stop offset="60%" stopColor="#5B8DEF" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#5B8DEF" stopOpacity="0" />
            </radialGradient>
            <filter id="dropShadow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="2.5" />
              <feOffset dx="0" dy="3" />
              <feComponentTransfer><feFuncA type="linear" slope="0.32" /></feComponentTransfer>
              <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>


          <g>
            {/* ground diamond */}
            <polygon
              points={`${pt(-1, -1)} ${pt(16, -1)} ${pt(16, 16)} ${pt(-1, 16)}`}
              fill="url(#groundGrad)"
              stroke="#B5BFAE"
              strokeWidth="1"
            />

            {/* roads */}
            <polygon
              points={`${pt(-1, 6.5)} ${pt(16, 6.5)} ${pt(16, 8.5)} ${pt(-1, 8.5)}`}
              fill="#4A4F58"
            />
            <polygon
              points={`${pt(6.5, -1)} ${pt(8.5, -1)} ${pt(8.5, 16)} ${pt(6.5, 16)}`}
              fill="#4A4F58"
            />

            {/* lane markings */}
            {Array.from({ length: 18 }).map((_, i) => {
              const x0 = -1 + i;
              const x1 = x0 + 0.45;
              return (
                <polygon
                  key={`lh${i}`}
                  points={`${pt(x0, 7.48)} ${pt(x1, 7.48)} ${pt(x1, 7.52)} ${pt(x0, 7.52)}`}
                  fill="#F4D35E"
                  className="lane"
                  style={{ animationDelay: `${i * 0.08}s` }}
                />
              );
            })}
            {Array.from({ length: 18 }).map((_, i) => {
              const y0 = -1 + i;
              const y1 = y0 + 0.45;
              return (
                <polygon
                  key={`lv${i}`}
                  points={`${pt(7.48, y0)} ${pt(7.52, y0)} ${pt(7.52, y1)} ${pt(7.48, y1)}`}
                  fill="#F4D35E"
                  className="lane"
                  style={{ animationDelay: `${i * 0.08}s` }}
                />
              );
            })}

            {/* sidewalks */}
            <polygon points={`${pt(-1, 6.2)} ${pt(16, 6.2)} ${pt(16, 6.5)} ${pt(-1, 6.5)}`} fill="#D8DCE3" />
            <polygon points={`${pt(-1, 8.5)} ${pt(16, 8.5)} ${pt(16, 8.8)} ${pt(-1, 8.8)}`} fill="#D8DCE3" />
            <polygon points={`${pt(6.2, -1)} ${pt(6.5, -1)} ${pt(6.5, 16)} ${pt(6.2, 16)}`} fill="#D8DCE3" />
            <polygon points={`${pt(8.5, -1)} ${pt(8.8, -1)} ${pt(8.8, 16)} ${pt(8.5, 16)}`} fill="#D8DCE3" />

            {/* AI halo */}
            <circle cx={aiCenter[0]} cy={aiCenter[1] - 30} r="180" fill="url(#aiGlow)" className="ai-halo" />

            {/* depth-sorted 3D items: buildings, cars, trees, people - prevents overlap bugs */}
            {items.map((it) => (
              <React.Fragment key={it.key}>{it.el}</React.Fragment>
            ))}

            {/* grey branding signs projected onto south face of each traditional shop */}
            {BUILDINGS.filter((b) => !b.hasAI && b.h > 0.5).map((b) => {
              const u0 = 0.18;
              const u1 = b.w - 0.18;
              const v0 = b.h * 0.48;
              const v1 = b.h * 0.66;
              const yFace = b.y + b.d;
              const c1 = iso(b.x + u0, yFace, v0);
              const c2 = iso(b.x + u1, yFace, v0);
              const c3 = iso(b.x + u1, yFace, v1);
              const c4 = iso(b.x + u0, yFace, v1);
              /* text anchor at center of sign */
              const cu = (u0 + u1) / 2;
              const cv = (v0 + v1) / 2;
              const [tx, ty] = iso(b.x + cu, yFace, cv);
              return (
                <g key={`brand-${b.id}`}>
                  {/* sign plate (sheared rectangle = parallelogram on screen) */}
                  <polygon
                    points={`${c1.join(',')} ${c2.join(',')} ${c3.join(',')} ${c4.join(',')}`}
                    fill="#E8EAEE"
                    stroke="#6B7280"
                    strokeWidth="0.5"
                    opacity="0.92"
                  />
                  {/* subtle inner shadow strip at top */}
                  <polygon
                    points={`${c4.join(',')} ${c3.join(',')} ${iso(b.x + u1, yFace, v1 - (v1 - v0) * 0.18).join(',')} ${iso(b.x + u0, yFace, v1 - (v1 - v0) * 0.18).join(',')}`}
                    fill="#9CA3AF"
                    opacity="0.35"
                  />
                  {/* bracket mounts (small bolts at sign corners) */}
                  <circle cx={c1[0] + 2} cy={c1[1] - 1} r="0.9" fill="#4B5563" />
                  <circle cx={c2[0] - 2} cy={c2[1] - 1} r="0.9" fill="#4B5563" />
                  <circle cx={c3[0] - 2} cy={c3[1] + 1} r="0.9" fill="#4B5563" />
                  <circle cx={c4[0] + 2} cy={c4[1] + 1} r="0.9" fill="#4B5563" />
                  {/* shop name text projected onto face via shear matrix */}
                  <text
                    transform={`matrix(1, 0.5, 0, 1, ${tx}, ${ty})`}
                    x="0" y="3"
                    fontSize="9"
                    fontWeight="800"
                    textAnchor="middle"
                    fill="#4B5563"
                    fontFamily="system-ui, -apple-system, Segoe UI, Roboto, sans-serif"
                    letterSpacing="0.5"
                  >
                    {b.name.toUpperCase()}
                  </text>
                </g>
              );
            })}

            {/* sparkles rising from AI shop */}
            {[0, 1, 2, 3].map((i) => {
              const [sx, sy] = iso(ai.x + ai.w / 2 - 0.3 + i * 0.2, ai.y + ai.d / 2, ai.h + 0.6);
              return (
                <text
                  key={`sp${i}`}
                  x={sx}
                  y={sy}
                  fontSize="14"
                  fill="#5B8DEF"
                  className={`sparkle sparkle-${i}`}
                  style={{ animationDelay: `${i * 0.7}s` }}
                >
                  ✦
                </text>
              );
            })}

            {/* AI badge floating above */}
            <g className="ai-badge" transform={`translate(${aiTop[0] - 56}, ${aiTop[1] - 70})`}>
              <rect x="0" y="0" width="112" height="28" rx="14" fill="#0F172A" />
              <circle cx="14" cy="14" r="5" fill="#5B8DEF" className="pulse-dot" />
              <text x="26" y="18" fontFamily="system-ui, -apple-system, Segoe UI, Roboto" fontSize="11" fontWeight="700" fill="#FFFFFF">
                AI POWERED
              </text>
            </g>
          </g>

          {/* tooltip */}
          {hovered && (() => {
            const b = BUILDINGS.find((x) => x.id === hovered);
            if (!b) return null;
            const [tx, ty] = iso(b.x + b.w / 2, b.y + b.d / 2, b.h + 0.4);
            const text = b.hasAI ? 'Powered by AI · Queue out the door' : 'Traditional shop · few walk-ins';
            return (
              <g className="tooltip" transform={`translate(${tx - 100}, ${ty - 50})`}>
                <rect x="0" y="0" width="200" height="40" rx="8" fill="#0F172A" opacity="0.96" />
                <text x="12" y="17" fontSize="11" fontWeight="700" fill="#FFFFFF" fontFamily="system-ui">
                  {b.name}
                </text>
                <text x="12" y="31" fontSize="10" fill={b.hasAI ? '#7AB8FF' : '#A8B0BD'} fontFamily="system-ui">
                  {text}
                </text>
              </g>
            );
          })()}
        </svg>

      </motion.div>
    </div>
  );
};

/* ---------------- styles ---------------- */
const css = `
.ai-city-wrap {
  position: relative;
  width: 100%;
}
.ai-city-inner {
  position: relative;
  width: 100%;
}
.ai-city-svg {
  width: 100%;
  height: auto;
  display: block;
  font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
  shape-rendering: geometricPrecision;
}

.shop { transition: transform 0.35s cubic-bezier(.2,.8,.2,1); }
.shop.is-hovered { transform: translateY(-4px); }

.shop-ai > g { animation: bobShop 4s ease-in-out infinite; transform-box: fill-box; }
@keyframes bobShop {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-2px); }
}

.ai-halo {
  transform-origin: center;
  transform-box: fill-box;
  animation: halo 3.6s ease-in-out infinite;
}
@keyframes halo {
  0%, 100% { transform: scale(1);    opacity: 0.85; }
  50%      { transform: scale(1.08); opacity: 1; }
}

.pulse-dot {
  transform-origin: center;
  transform-box: fill-box;
  animation: pulse 1.4s ease-in-out infinite;
}
@keyframes pulse {
  0%, 100% { transform: scale(1);   opacity: 1; }
  50%      { transform: scale(1.6); opacity: 0.5; }
}

.signal-ring {
  transform-origin: center;
  transform-box: fill-box;
  animation: signalRing 2.4s ease-out infinite;
  opacity: 0;
}
@keyframes signalRing {
  0%   { transform: scale(0.4); opacity: 0; }
  20%  { opacity: 0.9; }
  100% { transform: scale(4);   opacity: 0; }
}

.led-dot {
  animation: ledBlink 2.4s ease-in-out infinite;
}
@keyframes ledBlink {
  0%, 100% { opacity: 0.25; }
  50%      { opacity: 1; }
}

.jet, .jet-top {
  animation: jet 1.6s ease-in-out infinite;
  transform-origin: center;
  transform-box: fill-box;
}
@keyframes jet {
  0%, 100% { opacity: 0.7; transform: scaleY(1); }
  50%      { opacity: 1;   transform: scaleY(1.15); }
}

.lamp-glow {
  animation: lampGlow 3s ease-in-out infinite;
  transform-origin: center;
  transform-box: fill-box;
}
@keyframes lampGlow {
  0%, 100% { opacity: 0.9; }
  50%      { opacity: 1; filter: drop-shadow(0 0 4px #FFD24A); }
}


.lane { animation: laneFlow 1.6s linear infinite; }
@keyframes laneFlow {
  0%, 100% { opacity: 0.9; }
  50%      { opacity: 0.45; }
}

.cloud-a { animation: drift 50s linear infinite; }
.cloud-b { animation: drift 70s linear infinite reverse; }
@keyframes drift {
  0%   { transform: translateX(-100px); }
  100% { transform: translateX(1100px); }
}

.bob { animation: bobP 1.4s ease-in-out infinite; transform-box: fill-box; transform-origin: center bottom; }
@keyframes bobP {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-2px); }
}

@keyframes walkPath {
  0%   { offset-distance: 0%; }
  100% { offset-distance: 100%; }
}

/* 12s traffic-light cycle. h-axis green 0-6s, v-axis green 6-12s.
   h-cars (c-3, turn): delay 0. v-cars (c-4, c-5): delay 6s. */
.c-3 { animation: carHback 12s linear infinite; }
.c-4 { animation: carVfwd  12s linear -6s infinite; }
.c-5 { animation: carVback 12s linear -6s infinite; }

/* 12s cycle for all cars. h-axis cars cross intersection at ~50% (mid-cycle).
   v-axis cars offset by -6s so they cross intersection at the OPPOSITE phase (cycle wrap).
   stop line is at translate(-31,-16) for west entry, etc — just before intersection edge. */
@keyframes carHfwd {
  0%   { transform: translate(-260px, -130px); }
  22%  { transform: translate(-60px, -30px); }
  30%  { transform: translate(-31px, -16px); }   /* STOP at west stop line */
  42%  { transform: translate(-31px, -16px); }   /* WAIT at red */
  48%  { transform: translate(-15px, -8px); }
  55%  { transform: translate(31px, 16px); }     /* exit east side */
  100% { transform: translate(260px, 130px); }
}
@keyframes carHback {
  0%   { transform: translate(260px, 130px); }
  22%  { transform: translate(60px, 30px); }
  30%  { transform: translate(31px, 16px); }     /* STOP at east stop line */
  42%  { transform: translate(31px, 16px); }     /* WAIT */
  48%  { transform: translate(15px, 8px); }
  55%  { transform: translate(-31px, -16px); }
  100% { transform: translate(-260px, -130px); }
}
@keyframes carVfwd {
  0%   { transform: translate(260px, -130px); }
  22%  { transform: translate(60px, -30px); }
  30%  { transform: translate(31px, -16px); }    /* STOP at north stop line */
  42%  { transform: translate(31px, -16px); }
  48%  { transform: translate(15px, -8px); }
  55%  { transform: translate(-31px, 16px); }
  100% { transform: translate(-260px, 130px); }
}
@keyframes carVback {
  0%   { transform: translate(-260px, 130px); }
  22%  { transform: translate(-60px, 30px); }
  30%  { transform: translate(-31px, 16px); }    /* STOP at south stop line */
  42%  { transform: translate(-31px, 16px); }
  48%  { transform: translate(-15px, 8px); }
  55%  { transform: translate(31px, -16px); }
  100% { transform: translate(260px, -130px); }
}

/* turning car: east-bound enters, stops at light, turns right (south) and exits south.
   Fade-swap between h-shape and v-shape at intersection center. */
.turn-h-part {
  animation: turnHpos 20s linear infinite, turnHfade 20s linear infinite;
}
.turn-v-part {
  animation: turnVpos 20s linear infinite, turnVfade 20s linear infinite;
}
/* turning car: synced with h-axis (12s cycle, delay 0). Stops at west stop line,
   advances into intersection for turn, fade-swaps to south-bound, exits during v-green half. */
@keyframes turnHpos {
  0%   { transform: translate(-260px, -130px); }
  22%  { transform: translate(-60px, -30px); }
  30%  { transform: translate(-31px, -16px); }   /* STOP at west stop line */
  42%  { transform: translate(-31px, -16px); }   /* WAIT */
  48%  { transform: translate(-15px, -8px); }
  55%  { transform: translate(0, 0); }           /* arrive at intersection center */
  100% { transform: translate(0, 0); }
}
@keyframes turnHfade {
  0%, 56%   { opacity: 1; }
  58%, 100% { opacity: 0; }
}
@keyframes turnVpos {
  0%, 55%  { transform: translate(0, 0); }       /* parked at intersection center */
  62%      { transform: translate(-8px, 4px); }
  70%      { transform: translate(-31px, 16px); }   /* exit south stop line */
  100%     { transform: translate(-260px, 130px); } /* far south */
}
@keyframes turnVfade {
  0%, 55%  { opacity: 0; }
  57%, 100% { opacity: 1; }
}

.sparkle { opacity: 0; animation: rise 2.8s ease-out infinite; transform-box: fill-box; }
@keyframes rise {
  0%   { opacity: 0; transform: translateY(0)   scale(0.7); }
  25%  { opacity: 1; }
  100% { opacity: 0; transform: translateY(-50px) scale(1.2); }
}

.tooltip { pointer-events: none; animation: tipIn 0.2s ease-out; }
@keyframes tipIn {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; }
}

.tree-sway { transform-box: fill-box; transform-origin: center bottom; animation: sway 5s ease-in-out infinite; }
@keyframes sway {
  0%, 100% { transform: rotate(-1.5deg); }
  50%      { transform: rotate(1.5deg); }
}

.ai-city-legend {
  position: absolute;
  left: 24px;
  bottom: 18px;
  display: flex;
  gap: 16px;
  align-items: center;
  background: rgba(255,255,255,0.94);
  backdrop-filter: blur(6px);
  padding: 8px 14px;
  border-radius: 999px;
  box-shadow: 0 8px 20px -8px rgba(15,23,42,0.2);
  font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
  font-size: 12px;
  color: #1F2937;
}
.legend-item { display: flex; align-items: center; gap: 6px; font-weight: 600; }
.dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
.dot-ai    { background: #5B8DEF; box-shadow: 0 0 0 3px rgba(91,141,239,0.25); }
.dot-other { background: #C97B63; }
.legend-hint { color: #6B7280; font-size: 11px; }

@media (max-width: 640px) {
  .ai-city-legend { left: 50%; transform: translateX(-50%); font-size: 11px; }
  .legend-hint { display: none; }
}

@media (prefers-reduced-motion: reduce) {
  .ai-city-svg * { animation: none !important; }
}
`;

export default AICityScene;
